import React, { useEffect, useRef, useState } from "react"
import { LinearCopy } from "gl-react"
import { Surface } from "gl-react-dom"
import GLTransition from "react-gl-transition"
import GLTransitions from "gl-transitions"
import { Box } from "theme-ui"
import ContainerDimensions from "react-container-dimensions"
import { useBreakpointIndex } from "@theme-ui/match-media"

const Transition = ({ slides, currentSlide, setCurrentSlide }) => {
  const breakpointIndex = useBreakpointIndex()
  const [images, setImages] = useState(
    slides.map(slide => {
      return breakpointIndex === 0
        ? slide.image.mobile.images.fallback.src
        : slide.image.gatsbyImageData.images.fallback.src
    })
  )
  // const [mouseDown, setMouseDown] = useState(false)
  // const [width, height] = useWindowDimension()
  const [from, setFrom] = useState(images[0])
  const [to, setTo] = useState(images[1])
  const [progress, setProgess] = useState(0)
  const duration = 8000
  const refreshRate = 24
  const tick = 100 / duration
  const interval = useRef(null)
  const reverse = useRef(null)

  useEffect(() => {
    setImages(
      slides.map(slide => {
        return breakpointIndex === 0
          ? slide.image.mobile.images.fallback.src
          : slide.image.gatsbyImageData.images.fallback.src
      })
    )
  }, [breakpointIndex])

  useEffect(() => {
    if (progress > 1) nextSlide()
    if (progress < 0) stopReverseCounter()
  }, [progress])

  useEffect(() => {
    updateImage()
  }, [currentSlide, images])

  // useEffect(() => {
  //   setFrom(images[0])
  //   setTo(images[1])
  // }, [images])

  const updateImage = () => {
    setProgess(0)
    setFrom(images[currentSlide])
    if (currentSlide === images.length - 1) {
      setTo(images[0])
    } else {
      setTo(images[currentSlide + 1])
    }
  }

  const nextSlide = () => {
    stopAnimation()
    if (currentSlide === images.length - 1) {
      setCurrentSlide(0)
    } else {
      setCurrentSlide(currentSlide => currentSlide + 1)
    }
  }

  const startAnimation = () =>
    (interval.current = setInterval(() => {
      setProgess(prevState => prevState + tick)
    }, refreshRate))

  const reverseCounter = () =>
    (reverse.current = setInterval(() => {
      setProgess(prevState => prevState - tick)
    }, refreshRate))

  const stopAnimation = () => {
    clearInterval(interval.current)
  }

  const stopReverseCounter = () => {
    clearInterval(reverse.current)
  }

  const coverSizeWidth = (width, height) => {
    if (width < 640) {
      // console.log("<1")
      return height
    }
    if (width / height < 1) {
      // console.log("<1")
      return height * 1.77
    }
    if (width / height < 1.6) {
      // console.log("<2")
      return height * 1.77
    }
    if (width / height < 1.77) {
      // console.log("<2")
      return width
    }
    if (width / height >= 1.77) {
      // console.log(">=2")
      return width
    }
    return height * 1.77
  }

  const coverSizeHeight = (width, height) => {
    if (width < 640) {
      // console.log("<1")
      return height * 1.1
    }
    if (width / height < 1) {
      return height
    }
    if (width / height < 2) {
      return height
    }
    if (width / height >= 2) {
      return width / 1.77
    }
    return height
  }

  return (
    <ContainerDimensions>
      {({ height, width }) => (
        <Box
          sx={{ position: "relative" }}
          onClick={() => {
            if (progress === 0) {
              startAnimation()
            }
          }}
        >
          <Surface
            width={coverSizeWidth(width, height)}
            height={coverSizeHeight(width, height)}
            resizeMode="cover"
            style={{
              paddingLeft: 0,
              paddingRight: 0,
              marginLeft: "auto",
              marginRight: "auto",
              display: "block",
            }}
          >
            <Slideshow progress={progress} from={from} to={to} />
          </Surface>
        </Box>
      )}
    </ContainerDimensions>
  )
}

const Slideshow = ({ from, to, progress }) => {
  const transition = GLTransitions[7]

  const transitionCustom = {
    name: "CrossZoom",
    paramsTypes: {
      strength: "float",
    },
    defaultParams: {
      strength: 0.4,
    },
    glsl:
      "// License: MIT\n// Author: rectalogic\n// ported by gre from https://gist.github.com/rectalogic/b86b90161503a0023231\n\n// Converted from https://github.com/rectalogic/rendermix-basic-effects/blob/master/assets/com/rendermix/CrossZoom/CrossZoom.frag\n// Which is based on https://github.com/evanw/glfx.js/blob/master/src/filters/blur/zoomblur.js\n// With additional easing functions from https://github.com/rectalogic/rendermix-basic-effects/blob/master/assets/com/rendermix/Easing/Easing.glsllib\n\nuniform float strength; // = 0.4\n\nconst float PI = 3.141592653589793;\n\nfloat Linear_ease(in float begin, in float change, in float duration, in float time) {\n    return change * time / duration + begin;\n}\n\nfloat Exponential_easeInOut(in float begin, in float change, in float duration, in float time) {\n    if (time == 0.0)\n        return begin;\n    else if (time == duration)\n        return begin + change;\n    time = time / (duration / 2.0);\n    if (time < 1.0)\n        return change / 2.0 * pow(2.0, 10.0 * (time - 1.0)) + begin;\n    return change / 2.0 * (-pow(2.0, -10.0 * (time - 1.0)) + 2.0) + begin;\n}\n\nfloat Sinusoidal_easeInOut(in float begin, in float change, in float duration, in float time) {\n    return -change / 2.0 * (cos(PI * time / duration) - 1.0) + begin;\n}\n\nfloat rand (vec2 co) {\n  return fract(sin(dot(co.xy ,vec2(12.9898,78.233))) * 43758.5453);\n}\n\nvec3 crossFade(in vec2 uv, in float dissolve) {\n    return mix(getFromColor(uv).rgb, getToColor(uv).rgb, dissolve);\n}\n\nvec4 transition(vec2 uv) {\n    vec2 texCoord = uv.xy / vec2(1.0).xy;\n\n    // Linear interpolate center across center half of the image\n    vec2 center = vec2(Linear_ease(0.25, 0.5, 1.0, progress), 0.5);\n    float dissolve = Exponential_easeInOut(0.0, 1.0, 1.0, progress);\n\n    // Mirrored sinusoidal loop. 0->strength then strength->0\n    float strength = Sinusoidal_easeInOut(0.0, strength, 0.5, progress);\n\n    vec3 color = vec3(0.0);\n    float total = 0.0;\n    vec2 toCenter = center - texCoord;\n\n    /* randomize the lookup values to hide the fixed number of samples */\n    float offset = rand(uv);\n\n    for (float t = 0.0; t <= 40.0; t++) {\n        float percent = (t + offset) / 40.0;\n        float weight = 4.0 * (percent - percent * percent);\n        color += crossFade(texCoord + toCenter * percent * strength, dissolve) * weight;\n        total += weight;\n    }\n    return vec4(color / total, 1.0);\n}\n",
    license: "MIT",
    author: "rectalogic",
    createdAt: "Mon, 12 Jun 2017 12:33:07 +0800",
    updatedAt: "Mon, 12 Jun 2017 12:33:07 +0800",
  }

  return progress > 0 ? (
    <GLTransition
      from={from}
      to={to}
      progress={progress}
      transition={transition}
      options={{ resizeMode: "cover" }}
    />
  ) : (
    <LinearCopy>{from}</LinearCopy>
  )
}

export default Transition
